// @flow
import { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Container from '../CatalogFiltersContainer';
import MetalFilters from './MetalFilters';
import StyleFilters from './StyleFilters';
import DesignFilters from './DesignFilters';
import PriceFilters from './PriceFilters';
import ContourFilter from './ContourFilter';

import css from '@styles/bandFilters';

type TProps = {
  priceExtents: Array<number | string>
};

function Filters ({ priceExtents }: TProps) {
  const filters = useSelector((state) => state.bandSearchFilters);
  const [openTab, setOpenTab] = useState(null);
  const dispatch = useDispatch();

  const handleFilterCheckToggle = useCallback(
    (group, filterName) => {
      dispatch({
        type: 'TOGGLE_BAND_FILTER',
        payload: {
          group,
          filterName
        }
      });

      dispatch({ type: 'SYNC_URI_WITH_BAND_FILTERS' });
    },
    [dispatch]
  );

  const handleFilterRadioToggle = useCallback((filterName, value) => {
    dispatch({
      type: 'SET_BAND_FILTER',
      payload: {
        filterName,
        value
      }
    });

    dispatch({ type: 'SYNC_URI_WITH_BAND_FILTERS' });
  }, []);

  const handlePriceFilterChange = useCallback(
    (values) => {
      dispatch({
        type: 'SET_BAND_FILTER',
        payload: {
          filterName: 'price',
          value: values
        }
      });

      dispatch({ type: 'SYNC_URI_WITH_BAND_FILTERS' });
    },
    [priceExtents, dispatch]
  );

  const handleContourFilterChange = useCallback(
    (value) => {
      dispatch({
        type: 'SET_BAND_FILTER',
        payload: {
          filterName: 'contour',
          value
        }
      });

      dispatch({ type: 'SYNC_URI_WITH_BAND_FILTERS' });
    },
    [dispatch]
  );

  const toggleActiveTab = useCallback(
    (tab) => {
      if (openTab === tab) {
        setOpenTab(null);
      } else {
        setOpenTab(tab);
      }
    },
    [openTab]
  );

  const handleToggleMetalTab = useCallback(
    () => {
      toggleActiveTab('metal');
    },
    [toggleActiveTab]
  );

  const handleToggleStyleTab = useCallback(
    () => {
      toggleActiveTab('style');
    },
    [toggleActiveTab]
  );

  const handleToggleDesignTab = useCallback(
    () => {
      toggleActiveTab('design');
    },
    [toggleActiveTab]
  );

  const handleTogglePriceRangeTab = useCallback(
    () => {
      toggleActiveTab('priceRange');
    },
    [toggleActiveTab]
  );

  return (
    <Container className={css.container}>
      <MetalFilters
        selectedItems={filters.metal}
        onFilterCheckToggle={handleFilterCheckToggle}
        onToggle={handleToggleMetalTab}
        isOpen={openTab === 'metal'}
        key="metal-filters"
      />
      <StyleFilters
        selectedItems={[...filters.style, ...filters.gender]}
        onFilterCheckToggle={handleFilterCheckToggle}
        onToggle={handleToggleStyleTab}
        isOpen={openTab === 'style'}
        key="style-filters"
      />

      <br />

      <DesignFilters
        selectedItems={filters.design}
        onFilterCheckToggle={handleFilterCheckToggle}
        onToggle={handleToggleDesignTab}
        isOpen={openTab === 'design'}
        key="design-filters"
      />

      <br />

      <PriceFilters
        priceValues={filters.price}
        priceExtents={priceExtents}
        sortDirection={filters.sort}
        onPriceFilterChange={handlePriceFilterChange}
        onRadioFilterChange={handleFilterRadioToggle}
        onToggle={handleTogglePriceRangeTab}
        isOpen={openTab === 'priceRange'}
        key="price-filters"
      />

      <br />

      <ContourFilter
        checked={filters.contour}
        onChange={handleContourFilterChange}
        key="contour-filters"
      />
    </Container>
  );
}

export default Filters;
